import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, API } from "../../lib/api";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Save, Plus, Trash2 } from "lucide-react";
import ImageUploader from "../../components/ImageUploader";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#E5EFE8] rounded-2xl p-4 sm:p-6">
      <div className="font-display text-lg font-semibold text-[#111C15] mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#5C7063]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Area({ value, onChange, rows = 3, testid }) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full border border-[#E5EFE8] rounded-md p-2 text-sm"
      data-testid={testid}
    />
  );
}

function Repeater({ items, onChange, blank, addLabel, testidPrefix, children }) {
  return (
    <div className="space-y-3">
      {(items || []).map((item, i) => (
        <div key={i} className="border-t border-[#F3F7F4] pt-3">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#5C7063]">
              #{i + 1}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              data-testid={`${testidPrefix}-remove-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          {children(item, (k, v) => {
            const arr = [...items];
            arr[i] = { ...arr[i], [k]: v };
            onChange(arr);
          }, i)}
        </div>
      ))}
      <Button
        size="sm"
        variant="outline"
        onClick={() => onChange([...(items || []), blank])}
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="w-3 h-3 mr-1" /> {addLabel}
      </Button>
    </div>
  );
}

export default function AdminContentTab() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/site`)
      .then((r) => setContent(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !content) return <div className="text-[#5C7063]">Loading…</div>;

  const setBlock = (block, k, v) =>
    setContent((c) => ({
      ...c,
      landing: { ...c.landing, [block]: { ...c.landing[block], [k]: v } },
    }));
  const setFooter = (k, v) => setContent((c) => ({ ...c, footer: { ...c.footer, [k]: v } }));
  const setNavLinks = (arr) => setContent((c) => ({ ...c, nav: { ...c.nav, links: arr } }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/site`, { content });
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hero = content.landing.hero || {};
  const steps = content.landing.steps || {};
  const feature = content.landing.feature || {};
  const tms = content.landing.testimonials || {};
  const band = content.landing.code_band || {};
  const cta = content.landing.cta || {};
  const footer = content.footer || {};

  const SaveBtn = ({ testid }) => (
    <Button
      onClick={save}
      disabled={saving}
      className="bg-[#10B981] hover:bg-[#059669] text-white h-12 w-full sm:w-auto"
      data-testid={testid}
    >
      <Save className="w-4 h-4 mr-2" />
      {saving ? "Saving…" : "Save changes"}
    </Button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#111C15]">
          Website content
        </h2>
        <SaveBtn testid="save-content-btn" />
      </div>

      <Section title="Navigation links">
        <Repeater
          items={content.nav?.links}
          onChange={setNavLinks}
          blank={{ label: "", href: "/" }}
          addLabel="Add link"
          testidPrefix="nav"
        >
          {(l, upd, i) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Label">
                <Input
                  value={l.label}
                  onChange={(e) => upd("label", e.target.value)}
                  data-testid={`nav-label-${i}`}
                />
              </Field>
              <Field label="Link (e.g. /#meals)">
                <Input
                  value={l.href}
                  onChange={(e) => upd("href", e.target.value)}
                  data-testid={`nav-href-${i}`}
                />
              </Field>
            </div>
          )}
        </Repeater>
      </Section>

      <Section title="Hero">
        <Field label="Kicker (small green label)">
          <Input
            value={hero.kicker || ""}
            onChange={(e) => setBlock("hero", "kicker", e.target.value)}
            data-testid="hero-kicker"
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Headline line 1 (dark)">
            <Input
              value={hero.title_prefix || ""}
              onChange={(e) => setBlock("hero", "title_prefix", e.target.value)}
              data-testid="hero-title-prefix"
            />
          </Field>
          <Field label="Headline line 2 (green)">
            <Input
              value={hero.title_highlight || ""}
              onChange={(e) => setBlock("hero", "title_highlight", e.target.value)}
              data-testid="hero-title-highlight"
            />
          </Field>
        </div>
        <Field label="Subtitle">
          <Area
            value={hero.subtitle}
            onChange={(v) => setBlock("hero", "subtitle", v)}
            testid="hero-subtitle"
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary button">
            <Input
              value={hero.primary_cta || ""}
              onChange={(e) => setBlock("hero", "primary_cta", e.target.value)}
              data-testid="hero-primary-cta"
            />
          </Field>
          <Field label="Secondary button">
            <Input
              value={hero.secondary_cta || ""}
              onChange={(e) => setBlock("hero", "secondary_cta", e.target.value)}
              data-testid="hero-secondary-cta"
            />
          </Field>
          <Field label="Price">
            <Input
              value={hero.price || ""}
              onChange={(e) => setBlock("hero", "price", e.target.value)}
              data-testid="hero-price"
            />
          </Field>
          <Field label="Price note">
            <Input
              value={hero.price_note || ""}
              onChange={(e) => setBlock("hero", "price_note", e.target.value)}
              data-testid="hero-price-note"
            />
          </Field>
        </div>
        <Field label="Hero photo (arch-topped image)">
          <ImageUploader
            value={hero.image_url || ""}
            onChange={(v) => setBlock("hero", "image_url", v)}
            height="h-48"
            testId="hero-image"
          />
        </Field>
        <Field label="Lime circle badge text">
          <Input
            value={hero.badge || ""}
            onChange={(e) => setBlock("hero", "badge", e.target.value)}
            data-testid="hero-badge"
          />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Floating card label">
            <Input
              value={hero.card_label || ""}
              onChange={(e) => setBlock("hero", "card_label", e.target.value)}
              data-testid="hero-card-label"
            />
          </Field>
          <Field label="Floating card title">
            <Input
              value={hero.card_title || ""}
              onChange={(e) => setBlock("hero", "card_title", e.target.value)}
              data-testid="hero-card-title"
            />
          </Field>
          <Field label="Floating card note">
            <Input
              value={hero.card_note || ""}
              onChange={(e) => setBlock("hero", "card_note", e.target.value)}
              data-testid="hero-card-note"
            />
          </Field>
        </div>
      </Section>

      <Section title="Green benefit bar">
        <div className="space-y-2">
          {(content.landing.benefits || []).map((b, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={b}
                onChange={(e) => {
                  const arr = [...content.landing.benefits];
                  arr[i] = e.target.value;
                  setContent((c) => ({ ...c, landing: { ...c.landing, benefits: arr } }));
                }}
                data-testid={`benefit-${i}`}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setContent((c) => ({
                    ...c,
                    landing: { ...c.landing, benefits: c.landing.benefits.filter((_, x) => x !== i) },
                  }))
                }
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setContent((c) => ({
                ...c,
                landing: { ...c.landing, benefits: [...(c.landing.benefits || []), ""] },
              }))
            }
            data-testid="benefit-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Add benefit
          </Button>
        </div>
      </Section>

      <Section title="Four easy steps">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <Input
              value={steps.kicker || ""}
              onChange={(e) => setBlock("steps", "kicker", e.target.value)}
              data-testid="steps-kicker"
            />
          </Field>
          <Field label="Title">
            <Input
              value={steps.title || ""}
              onChange={(e) => setBlock("steps", "title", e.target.value)}
              data-testid="steps-title"
            />
          </Field>
        </div>
        <Field label="Subtitle">
          <Input
            value={steps.subtitle || ""}
            onChange={(e) => setBlock("steps", "subtitle", e.target.value)}
            data-testid="steps-subtitle"
          />
        </Field>
        <Repeater
          items={steps.items}
          onChange={(arr) => setBlock("steps", "items", arr)}
          blank={{ n: "", t: "", d: "" }}
          addLabel="Add step"
          testidPrefix="step"
        >
          {(s, upd, i) => (
            <div className="grid sm:grid-cols-12 gap-3">
              <div className="sm:col-span-2">
                <Field label="Number">
                  <Input value={s.n} onChange={(e) => upd("n", e.target.value)} data-testid={`step-n-${i}`} />
                </Field>
              </div>
              <div className="sm:col-span-4">
                <Field label="Title">
                  <Input value={s.t} onChange={(e) => upd("t", e.target.value)} data-testid={`step-t-${i}`} />
                </Field>
              </div>
              <div className="sm:col-span-6">
                <Field label="Description">
                  <Input value={s.d} onChange={(e) => upd("d", e.target.value)} data-testid={`step-d-${i}`} />
                </Field>
              </div>
            </div>
          )}
        </Repeater>
      </Section>

      <Section title="Sage feature band">
        <Field label="Kicker">
          <Input
            value={feature.kicker || ""}
            onChange={(e) => setBlock("feature", "kicker", e.target.value)}
            data-testid="feature-kicker"
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Headline part 1">
            <Input
              value={feature.title_prefix || ""}
              onChange={(e) => setBlock("feature", "title_prefix", e.target.value)}
              data-testid="feature-title-prefix"
            />
          </Field>
          <Field label="Headline part 2">
            <Input
              value={feature.title_highlight || ""}
              onChange={(e) => setBlock("feature", "title_highlight", e.target.value)}
              data-testid="feature-title-highlight"
            />
          </Field>
        </div>
        <Field label="Body text">
          <Area
            value={feature.body}
            onChange={(v) => setBlock("feature", "body", v)}
            testid="feature-body"
          />
        </Field>
        <Repeater
          items={feature.bullets}
          onChange={(arr) => setBlock("feature", "bullets", arr)}
          blank={{ lead: "", rest: "" }}
          addLabel="Add bullet"
          testidPrefix="fbullet"
        >
          {(b, upd, i) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Bold lead-in">
                <Input value={b.lead} onChange={(e) => upd("lead", e.target.value)} data-testid={`fbullet-lead-${i}`} />
              </Field>
              <Field label="Rest of the line">
                <Input value={b.rest} onChange={(e) => upd("rest", e.target.value)} data-testid={`fbullet-rest-${i}`} />
              </Field>
            </div>
          )}
        </Repeater>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Button label">
            <Input
              value={feature.cta || ""}
              onChange={(e) => setBlock("feature", "cta", e.target.value)}
              data-testid="feature-cta"
            />
          </Field>
          <Field label="White tag on the photo">
            <Input
              value={feature.image_tag || ""}
              onChange={(e) => setBlock("feature", "image_tag", e.target.value)}
              data-testid="feature-image-tag"
            />
          </Field>
        </div>
        <Field label="Photo">
          <ImageUploader
            value={feature.image_url || ""}
            onChange={(v) => setBlock("feature", "image_url", v)}
            height="h-40"
            testId="feature-image"
          />
        </Field>
      </Section>

      <Section title="Testimonials">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <Input
              value={tms.kicker || ""}
              onChange={(e) => setBlock("testimonials", "kicker", e.target.value)}
              data-testid="tms-kicker"
            />
          </Field>
          <Field label="Title">
            <Input
              value={tms.title || ""}
              onChange={(e) => setBlock("testimonials", "title", e.target.value)}
              data-testid="tms-title"
            />
          </Field>
        </div>
        <Field label="Subtitle">
          <Input
            value={tms.subtitle || ""}
            onChange={(e) => setBlock("testimonials", "subtitle", e.target.value)}
            data-testid="tms-subtitle"
          />
        </Field>
        <Repeater
          items={tms.items}
          onChange={(arr) => setBlock("testimonials", "items", arr)}
          blank={{ quote: "", name: "" }}
          addLabel="Add testimonial"
          testidPrefix="tms"
        >
          {(t, upd, i) => (
            <div className="space-y-3">
              <Field label="Quote">
                <Area value={t.quote} onChange={(v) => upd("quote", v)} rows={2} testid={`tms-quote-${i}`} />
              </Field>
              <Field label="Client name">
                <Input value={t.name} onChange={(e) => upd("name", e.target.value)} data-testid={`tms-name-${i}`} />
              </Field>
            </div>
          )}
        </Repeater>
      </Section>

      <Section title="Client code band">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <Input
              value={band.kicker || ""}
              onChange={(e) => setBlock("code_band", "kicker", e.target.value)}
              data-testid="band-kicker"
            />
          </Field>
          <Field label="Title">
            <Input
              value={band.title || ""}
              onChange={(e) => setBlock("code_band", "title", e.target.value)}
              data-testid="band-title"
            />
          </Field>
          <Field label="Subtitle">
            <Input
              value={band.subtitle || ""}
              onChange={(e) => setBlock("code_band", "subtitle", e.target.value)}
              data-testid="band-subtitle"
            />
          </Field>
          <Field label="Button label">
            <Input
              value={band.cta || ""}
              onChange={(e) => setBlock("code_band", "cta", e.target.value)}
              data-testid="band-cta"
            />
          </Field>
        </div>
      </Section>

      <Section title="Closing green CTA">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <Input
              value={cta.kicker || ""}
              onChange={(e) => setBlock("cta", "kicker", e.target.value)}
              data-testid="cta-kicker"
            />
          </Field>
          <Field label="Title">
            <Input
              value={cta.title || ""}
              onChange={(e) => setBlock("cta", "title", e.target.value)}
              data-testid="cta-title"
            />
          </Field>
        </div>
        <Field label="Subtitle">
          <Input
            value={cta.subtitle || ""}
            onChange={(e) => setBlock("cta", "subtitle", e.target.value)}
            data-testid="cta-subtitle"
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary button (lime)">
            <Input
              value={cta.primary || ""}
              onChange={(e) => setBlock("cta", "primary", e.target.value)}
              data-testid="cta-primary"
            />
          </Field>
          <Field label="Secondary button">
            <Input
              value={cta.secondary || ""}
              onChange={(e) => setBlock("cta", "secondary", e.target.value)}
              data-testid="cta-secondary"
            />
          </Field>
        </div>
      </Section>

      <Section title="Footer">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Brand name">
            <Input
              value={footer.brand || ""}
              onChange={(e) => setFooter("brand", e.target.value)}
              data-testid="footer-brand"
            />
          </Field>
          <Field label="Tagline">
            <Input
              value={footer.tagline || ""}
              onChange={(e) => setFooter("tagline", e.target.value)}
              data-testid="footer-tagline"
            />
          </Field>
          <Field label="Support column label">
            <Input
              value={footer.support_label || ""}
              onChange={(e) => setFooter("support_label", e.target.value)}
              data-testid="footer-support-label"
            />
          </Field>
          <Field label="Support email">
            <Input
              value={footer.support_email || ""}
              onChange={(e) => setFooter("support_email", e.target.value)}
              data-testid="footer-support-email"
            />
          </Field>
        </div>
        <Field label="Copyright line">
          <Input
            value={footer.copyright || ""}
            onChange={(e) => setFooter("copyright", e.target.value)}
            data-testid="footer-copyright"
          />
        </Field>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#5C7063]">
            Extra footer lines
          </Label>
          <div className="mt-1.5 space-y-2">
            {(footer.lines || []).map((l, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={l}
                  onChange={(e) => {
                    const arr = [...footer.lines];
                    arr[i] = e.target.value;
                    setFooter("lines", arr);
                  }}
                  data-testid={`footer-line-${i}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setFooter("lines", footer.lines.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFooter("lines", [...(footer.lines || []), ""])}
              data-testid="footer-add-line"
            >
              <Plus className="w-3 h-3 mr-1" /> Add line
            </Button>
          </div>
        </div>
      </Section>

      <div className="flex justify-end">
        <SaveBtn testid="save-content-btn-bottom" />
      </div>
    </div>
  );
}
